<!--
 * @Author: Nemo
 * @Description: 响应式弹窗组件，在PC端以dialog形式居中显示，在移动端以popup形式从底部弹出
 * @Date: 2024-05-29
-->
<template>
  <component :is="currentComponent" ref="dialogComponent" :visible="isVisible" :title="dialogTitle" :type="dialogType"
    :close-on-click-mask="dialogCloseOnClickMask" :show-default-footer="dialogShowDefaultFooter"
    :confirm-text="dialogConfirmText" :cancel-text="dialogCancelText" :billing-fields="dialogBillingFields"
    :show-billing-form="dialogShowBillingForm" :order-info="orderInfo" @update:visible="handleVisibleChange"
    @close="handleClose" @confirm="handleConfirm" @cancel="handleCancel" @field-focus="handleFieldFocus"
    @field-change="handleFieldChange">
    <template v-if="$slots.default" slot>
      <slot></slot>
    </template>
    <template v-if="$slots.footer" slot="footer">
      <slot name="footer"></slot>
    </template>
  </component>
</template>

<script>
import ResponsiveDialogMobile from './mobile.vue';
import ResponsiveDialogPC from './pc.vue';

export default {
  name: 'responsive-dialog',
  components: {
    ResponsiveDialogPC,
    ResponsiveDialogMobile,
  },
  props: {
    orderInfo: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isMobile: /Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent),
      formData: {},
      isVisible: false,
      dialogTitle: '',
      dialogType: '',
      dialogCloseOnClickMask: true,
      dialogShowDefaultFooter: true,
      dialogConfirmText: '确认',
      dialogCancelText: '取消',
      dialogBillingFields: [],
      dialogShowBillingForm: false,
      // 按支付方式分别缓存用户输入的表单数据
      cachedFormData: {
        default: {}, // 默认缓存
        apple: {}, // Apple Pay缓存
        google: {}, // Google Pay缓存
      },
    };
  },
  computed: {
    currentComponent() {
      return this.isMobile ? 'ResponsiveDialogMobile' : 'ResponsiveDialogPC';
    },
  },
  mounted() {
    // 监听窗口大小变化，动态判断是否为移动设备
    window.addEventListener('resize', this.checkDevice);
    this.checkDevice();
  },
  beforeDestroy() {
    // 移除事件监听器
    window.removeEventListener('resize', this.checkDevice);
  },
  methods: {
    // 打开弹窗方法，接收配置参数
    open(options = {}) {
      // 设置弹窗属性
      this.dialogTitle = options.title || '';
      this.dialogType = options.type || '';
      this.dialogCloseOnClickMask = options.closeOnClickMask !== undefined ? options.closeOnClickMask : true;
      this.dialogShowDefaultFooter = options.showDefaultFooter !== undefined ? options.showDefaultFooter : true;
      this.dialogConfirmText = options.confirmText || '确认';
      this.dialogCancelText = options.cancelText || '取消';

      // 是否重置表单，默认为false，保留用户操作
      const resetForm = options.resetForm === true;

      // 根据支付类型获取对应的缓存数据
      const paymentType = this.dialogType || 'default';
      const currentCache = this.cachedFormData[paymentType] || {};

      // 设置账单表单相关属性
      let fieldsWithCache = [];
      if (options.billingFields && options.billingFields.length > 0) {
        // 如果需要重置表单，或者对应支付方式的缓存为空，则使用原始字段值
        if (resetForm || Object.keys(currentCache).length === 0) {
          fieldsWithCache = [...options.billingFields];
          // 如果是重置表单，清空当前支付方式的缓存
          if (resetForm) {
            this.cachedFormData[paymentType] = {};
          }
        } else {
          // 否则，将缓存的表单数据应用到字段中
          fieldsWithCache = options.billingFields.map(field => {
            const newField = { ...field };
            // 如果有缓存数据，则使用缓存的值
            if (currentCache[field.name] !== undefined) {
              newField.value = currentCache[field.name];
            }
            return newField;
          });
        }
      }

      this.dialogBillingFields = fieldsWithCache.length > 0 ? fieldsWithCache : (options.billingFields || []);
      this.dialogShowBillingForm = options.showBillingForm !== undefined ? options.showBillingForm : Boolean(options.billingFields && options.billingFields.length > 0);

      // 显示弹窗
      this.isVisible = true;
    },

    // 关闭弹窗方法
    close() {
      this.isVisible = false;
    },

    checkDevice() {
      // 除了根据UA判断，还可以根据屏幕宽度判断
      const screenWidth = window.innerWidth;
      this.isMobile = screenWidth <= 768 || /Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent);
    },

    handleVisibleChange(val) {
      this.isVisible = val;
    },

    handleClose() {
      this.isVisible = false;
      this.$emit('close');
    },

    handleCancel() {
      this.isVisible = false;
      this.$emit('cancel');
    },

    handleConfirm(formData) {
      if (formData) {
        this.formData = formData;
        // 更新对应支付方式的缓存
        const paymentType = this.dialogType || 'default';
        this.cachedFormData[paymentType] = { ...formData };
        this.$emit('confirm', formData);
      } else {
        this.$emit('confirm');
      }

      // 获取当前子组件引用
      const componentRef = this.$refs.dialogComponent;

      // 如果有子组件引用，并且有handleClose方法，则调用它来实现平滑关闭
      if (componentRef && typeof componentRef.handleClose === 'function') {
        componentRef.handleClose();
      } else {
        // 降级处理：直接关闭
        this.isVisible = false;
      }
    },

    handleFieldFocus(fieldName) {
      this.$emit('field-focus', fieldName);
    },

    handleFieldChange(fieldData) {
      // 实时更新对应支付方式的缓存字段值
      if (fieldData && fieldData.name) {
        const paymentType = this.dialogType || 'default';
        if (!this.cachedFormData[paymentType]) {
          this.cachedFormData[paymentType] = {};
        }
        this.cachedFormData[paymentType][fieldData.name] = fieldData.value;
      }
      this.$emit('field-change', fieldData);
    },

    getFormData() {
      return this.formData;
    },
  },
};
</script>

<style lang="scss" scoped>
/* ===== 共用样式 ===== */
.responsive-dialog-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  backdrop-filter: blur(4px);
}

.responsive-dialog-container {
  position: fixed;
  z-index: 2001;
  background: #FFFFFF;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

/* ===== PC端样式 ===== */
.pc-dialog {
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 852px;
  height: 679px;
  border-radius: 18px;
}

.pc-dialog .dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 10px;
}

.pc-dialog .dialog-title-wrapper {
  display: flex;
  align-items: center;
}

.pc-dialog .payment-icon {
  height: 32px;
  width: auto;
  margin-right: 16px;
}

.pc-dialog .dialog-title {
  font-weight: 600;
  font-size: 18px;
  color: #1A202C;
}

.pc-dialog .dialog-close {
  cursor: pointer;
  color: #8D98AF;
}

.pc-dialog .dialog-close:hover {
  color: #182058;
}

.pc-dialog .dialog-content {
  padding: 10px 24px;
  max-height: 540px;
  overflow-y: auto;
}

.pc-dialog .dialog-footer {
  display: flex;
  justify-content: flex-end;
  padding: 10px 24px 24px;
}

.pc-dialog .dialog-footer button {
  padding: 8px 16px;
  margin-left: 10px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  border: none;
}

.pc-dialog .dialog-cancel-btn {
  background: #F0F3F6;
  color: #182058;
}

.pc-dialog .dialog-cancel-btn:hover {
  background: #E5E8ED;
}

.pc-dialog .dialog-confirm-btn {
  background: #182058;
  color: white;
}

.pc-dialog .dialog-confirm-btn:hover {
  background: #0F1642;
}

/* ===== 移动端样式 ===== */
.mobile-popup {
  left: 0;
  bottom: 0;
  width: 100%;
  height: 39.25rem;
  border-radius: 20px 20px 0 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  text-align: left;
  padding: 24px 8px;
  animation: slide-up 0.3s ease;
}

.mobile-popup .popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.mobile-popup .popup-title-wrapper {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin: 0;
}

.mobile-popup .payment-icon {
  height: 32px;
  width: auto;
  margin-right: 16px;
}

.mobile-popup .popup-title {
  font-weight: 600;
  font-size: 16px;
  color: #1A202C;
}

.mobile-popup .popup-close {
  cursor: pointer;
  color: #8D98AF;
}

.mobile-popup .popup-content {
  flex: 1;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
  margin: 16px 0;
  border-radius: 8px;
  border: 1px solid #EDF1F7;
  padding: 24px 8px;
}

.mobile-popup .popup-footer {
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  background: #FFFFFF;
  padding-bottom: 16px;
}

.mobile-popup .popup-btn {
  width: 100%;
  height: 44px;
  border-radius: 8px;
  font-size: 16px;
  border: none;
  cursor: pointer;
}

.mobile-popup .popup-confirm-btn {
  background: #182058;
  color: white;
}

.mobile-popup .popup-cancel-btn {
  background: #F0F3F6;
  color: #182058;
}

.mobile-popup .popup-default-btn {
  background: #FFFFFF;
  color: #182058;
  border: 1px solid #D0D5DD;
}

/* ===== 动画 ===== */
@keyframes slide-up {
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
}

/* ===== 特定图标样式 ===== */
.apple-pay-icon {
  width: 50px !important;
}

.google-pay-icon {
  width: 60.16px !important;
}
</style>
